import axios from 'axios';

import AvailsProgress from '@/common/models/avails/AvailsProgress';
import DeliveryStatistics from '@/common/models/deliveryStatistics/DeliveryStatistics';
import Demographic from '@/common/models/demographics/Demographic';
import GeocodedLocation from '@/common/models/geo/GeocodedLocation';
import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
import Location from '@/common/models/geo/Location';
import Market from '@/common/models/geo/Market';
import LineItem from '@/common/models/lineitem/LineItem';
import Exchange from '@/common/models/memberships/Exchange';
import MembershipSettings from '@/common/models/memberships/MembershipSettings';
import Organization from '@/common/models/memberships/Organization';
import Permissions from '@/common/models/memberships/Permissions';
import Language from '@/common/models/misc-api-models/Language';
import MediaType from '@/common/models/misc-api-models/MediaType';
import Network from '@/common/models/misc-api-models/Network';
import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
import Package from '@/common/models/package/Package';
import Site from '@/common/models/site/Site';
import SiteInfo from '@/common/models/site/SiteInfo';
import TargetingCriterion from '@/common/models/targeting-criteria/TargetingCriterion';
import Unit from '@/common/models/unit/Unit';
import UnitInfo from '@/common/models/unit/UnitInfo';
import UnitPack from '@/common/models/unit/UnitPack';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { organizationDSPsEndpoint } from '@/org-app/api';
import ApiUtils from '@/utils/ApiUtils';
import LocalCache from '@/utils/LocalCache';

export const BaseApiUrl = '/api/ui/exch';

const sitesEndpoint = 'sites/';
const unitsEndpoint = 'units/';

const handleError = (errorResponse, errorCallback) => {
  errorCallback(ApiUtils.getError(errorResponse), errorResponse.response.status);
  console.error(errorResponse?.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
};

export default class APIProxy {
  _instance;
  constructor() {
    this.axiosInstanceInternal = null;
    this.axiosInstancePublicEndpointsInternal = null;
    this.apiUtils = new ApiUtils(() => this.axiosInstance);

    this.getLanguages = this.apiUtils.createGetAllFunction('/languages/', Language);
    this.getMediaTypes = this.apiUtils.createGetAllFunction('/mediatypes/', MediaType);
    this.getNetworks = this.apiUtils.createGetAllFunction('/networks/', Network);
    this.getTags = this.apiUtils.createApiFunction('/tags/', 'get');
    this.getDemographics = this.apiUtils.createGetAllFunction('/demographics/', Demographic);
    this.getGeofenceGroups = this.apiUtils.createGetAllFunction('/geofencegroups/', GeofenceGroup);
    this.getLocationsByLevel = this.apiUtils.createApiFunction('/locations/getlocationsbylevel', 'get');
    this.getLocations = this.apiUtils.createGetAllFunction('/locations/', Location);
    this.getMarkets = this.apiUtils.createGetAllFunction('/markets/', Market);
    this.getMarketsWithInventory = this.apiUtils.createApiFunction('/markets/withinventory', 'get', Market, true);
    this.getLocationRoots = this.apiUtils.createApiFunction('/locations/roots', 'get');
    this.getUnitsBriefUsingPost = this.apiUtils.createApiFunction(`${unitsEndpoint}brief`, 'post', UnitInfo, true);
    this.getSites = this.apiUtils.createGetAllFunction('/sites', Site);
    this.getSitesBriefUsingPost = this.apiUtils.createApiFunction(`${sitesEndpoint}brief`, 'post', SiteInfo, true);
    this.getFilterChanges = this.apiUtils.createApiFunction('unitfilters/filtervalues', 'post');
    this.getUnitFiltersForTargets = this.apiUtils.createApiFunction('/lineitems/unitfiltersfortargets', 'post');
    this.getClusteredUnits = this.apiUtils.createApiFunction('/units/clustered', 'post');
    this.getUnitsInGeofences = this.apiUtils.createApiFunction('/units/ingeofences', 'post');
    this.getUnitIDsInGeofences = this.apiUtils.createApiFunction('/units/ingeofences/ids', 'post');
    this.getUnitPacks = this.apiUtils.createGetAllFunction('/unitpacks', UnitPack);

    this.getPackages = this.apiUtils.createGetAllFunction('/packages/', Package);
    this.getPackage = this.apiUtils.createGetOneFunction('/packages/', Package);
    this.postPackages = this.apiUtils.createPostFunction('/packages/', Package);
    this.putPackage = this.apiUtils.createPutFunction('/packages/', Package);
    this.putPackageState = this.apiUtils.createPutFunction('/packages/', Package, (pack) => pack.toApiStateChange());
    this.deletePackage = this.apiUtils.createDeleteFunction('/packages/', Package);
    this.getPackageAudit = this.apiUtils.createGetAuditFunction('/packages/', 'Package');
    this.getOrganizationDSPs = this.apiUtils.createGetAllFunction(
      `${organizationDSPsEndpoint}?$expand=dsp,organization`,
      OrganizationDSP,
    );

    this.getTargetedUnitAndSiteIDs = this.apiUtils.createApiFunction(
      '/deliverystatistics/targetedids',
      'post',
      DeliveryStatistics,
      false,
    );
  }

  static get instance() {
    if (!this._instance) {
      this._instance = new APIProxy();
    }
    return this._instance;
  }

  static reset() {
    APIProxy.instance.axiosInstanceInternal = null;
  }

  get axiosInstance() {
    if (this.axiosInstanceInternal === null) {
      const exch = LocalCache.get('exchange');
      const exchID = exch != null ? Exchange.fromApi(JSON.parse(exch)).id : null;

      const headers = {};

      if (LocalCache.get('token') != null) {
        headers.Authorization = `Bearer ${LocalCache.get('token')}`;
      }
      if (exchID != null) {
        headers['ax-exchange-id'] = exchID;
      } else {
        // The curator orgs can call some of the exchange endpoints.
        const org = LocalCache.get('organization');
        headers['ax-exchange-id'] = org != null ? Organization.fromApi(org).exchangeID : null;
      }

      this.axiosInstanceInternal = axios.create({
        baseURL: BaseApiUrl,
        headers,
      });

      this.axiosInstanceInternal.interceptors.response.use(
        (response) => {
          if (response.status === 401) {
            GlobalEventBus.$emit(GlobalEvents.logout);
          } else if (response.status === 403) {
            GlobalEventBus.$emit(GlobalEvents.unauthorized);
          }
          return response;
        },
        (error) => {
          if (axios.isCancel(error)) {
            console.log('Request cancelled');
            return;
          }
          if (error.response.data != null && error.response.data.error === 'JWT signature is invalid') {
            GlobalEventBus.$emit(GlobalEvents.resetToken);
          } else if (error.response.request.responseURL.indexOf('/login') === -1) {
            if (error.response.status === 401) {
              if (!error.request.responseURL.includes('changepassword')) {
                GlobalEventBus.$emit(GlobalEvents.logout);
              }
            } else if (error.response.status === 403) {
              GlobalEventBus.$emit(GlobalEvents.unauthorized);
            }
          }
          return Promise.reject(error); // eslint-disable-line
        },
      );
    }
    return this.axiosInstanceInternal;
  }

  async getPermissions() {
    const res = await this.axiosInstance.get('permissions/').catch((err) => {
      throw ApiUtils.getError(err);
    });
    return Permissions.fromApi(res.data);
  }

  async getExchangeSettings() {
    const response = await this.axiosInstance.get('exchanges/settings/').catch((err) => {
      throw ApiUtils.getError(err);
    });

    return MembershipSettings.fromApi(response.data);
  }

  async getSuppliers({ queryParams = null }) {
    const response = await this.axiosInstance
      .get('exchanges/suppliers', {
        params: queryParams ? queryParams.toObject() : {},
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return response.data.map((x) => Organization.fromApi(x));
  }

  //
  // LINEITEMS
  //
  async getLineItems({ queryParams = null, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get('/lineitems/summary', {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return ApiUtils.getResult(response, LineItem);
  }

  async getLineItem({ lineItemId, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`/lineitems/${lineItemId}`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return LineItem.fromApi(response.data);
  }

  async getCPMForTargets({ params, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .post('/lineitems/calculatecpm', params, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return response.data;
  }

  async getEstimatedDeliveryStatistics(params, cancelTokenSource = null) {
    const { isPrivate = true, planUuid = null } = params;
    if (!isPrivate && planUuid === null)
      throw new Error('getEstimatedDeliveryStatistics needs a planUuid for a public plan');

    const endPoint = isPrivate ? '/deliverystatistics/estimate' : `/plans/${planUuid}/deliverystatisticsestimate`;
    const response = await this.axiosInstance
      .post(endPoint, params, { cancelToken: cancelTokenSource ? cancelTokenSource.token : null })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return DeliveryStatistics.fromApi(response.data);
  }

  async getEstimatedDeliveryStatisticsScreenAndSiteCount(params, cancelTokenSource = null) {
    const response = await this.axiosInstance
      .post('/deliverystatistics/count', params, { cancelToken: cancelTokenSource ? cancelTokenSource.token : null })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return DeliveryStatistics.fromApi(response.data);
  }

  //
  // AVAILS
  //
  async getAvails({ params }) {
    const response = await this.axiosInstance.post('/avails/getavails', params).catch((err) => {
      throw ApiUtils.getError(err);
    });

    return response.data;
  }

  getAvailsByUnitPost(params, successCallback, errorCallback) {
    const queryParams = params.checkForOptimizedImpressions ? '?optimize_impressions=true' : '';
    this.axiosInstance.post(`/avails/checkavailsbyunit${queryParams}`, params).then(
      (successResponse) => {
        successCallback(AvailsProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvailsByUnit(lineItemId, successCallback, errorCallback) {
    this.axiosInstance.get(`/avails/checkavailsbyunit/${lineItemId}`).then(
      (successResponse) => {
        successCallback(AvailsProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvailsExecutionById(availsExecutionId, successCallback, errorCallback) {
    this.axiosInstance.get(`/avails/checkavailsexecution/${availsExecutionId}`).then(
      (successResponse) => {
        successCallback(AvailsProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // TARGETING CRITERIA
  //
  async getAllTargetingCriteria() {
    const response = await this.axiosInstance
      .get('targetingcriteria', {
        params: {
          $expand: 'targetingcriterion_subtypes',
        },
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return response.data.map((x) => TargetingCriterion.fromApi(x));
  }

  //
  // LOCATIONS
  //
  async children(locationID, networkIDs = [], mediaOwnerIDs = [], cancelTokenSource = null) {
    const response = await this.axiosInstance
      .get(`locations/${locationID}/children`, {
        params: {
          network_ids: networkIDs.join(','),
          owner_ids: mediaOwnerIDs.join(','),
        },
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return response.data
      .map((x) => Location.fromApi(x))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }

  async treeSearch(searchTerm, countryIDs = [], mediaOwnerIDs = [], networkIDs = [], cancelTokenSource = null) {
    const response = await this.axiosInstance
      .get('locations/treesearch', {
        params: {
          searchterm: searchTerm,
          country_ids: countryIDs && countryIDs.length > 0 ? countryIDs.join(',') : null,
          network_ids: networkIDs && networkIDs.length > 0 ? networkIDs.join(',') : null,
          owner_ids: mediaOwnerIDs && mediaOwnerIDs.length > 0 ? mediaOwnerIDs.join(',') : null,
        },
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return {
      count: response.data.total_count,
      locations: response.data.result
        .map((x) => Location.fromApi(x))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    };
  }

  //
  // UNITS
  //
  async getGeocodedUnitsAndSitesForTargets(params, cancelTokenSource = null) {
    const response = await this.axiosInstance
      .post('lineitems/geocodedunitsandsitesfortargets', params, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    if (response == null) {
      return null;
    }
    return response.data.map((x) => GeocodedLocation.fromApi(x));
  }

  async getUnits(params) {
    const { queryParams, cancelTokenSource } = params;
    const data = ApiUtils.getBodyData(queryParams);
    const response = await this.axiosInstance
      .post(`${unitsEndpoint}postgetlist`, data, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    return ApiUtils.getResult(response, Unit);
  }
}
